import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY

let supabaseInstance = null;

if (!supabaseUrl || !supabaseKey || supabaseUrl.includes('YOUR_SUPABASE_URL')) {
    // Si faltan las llaves, mostramos un error visible y no crasheamos
    const msg = 'ERROR CRÍTICO: Faltan las llaves de Supabase en Vercel. Revisa Environment Variables.';
    console.error(msg);
    if (typeof window !== 'undefined') alert(msg);

    // Mock Supabase client to prevent crash
    supabaseInstance = {
        auth: {
            getUser: async () => ({ data: { user: null }, error: new Error(msg) }),
            getSession: async () => ({ data: { session: null }, error: null }),
            onAuthStateChange: () => ({ data: { subscription: { unsubscribe: () => { } } } }),
            signInWithPassword: async () => ({ data: null, error: new Error(msg) }),
            signUp: async () => ({ data: null, error: new Error(msg) }),
            signOut: async () => ({ error: null }),
        },
        from: () => ({
            select: () => ({ data: [], error: new Error(msg) }),
            insert: () => ({ data: null, error: new Error(msg) }),
            update: () => ({ data: null, error: new Error(msg) }),
            delete: () => ({ data: null, error: new Error(msg) }),
            upsert: () => ({ data: null, error: new Error(msg) }),
            eq: function () { return this; },
            order: function () { return this; },
            single: function () { return this; },
            limit: function () { return this; }
        })
    };
} else {
    supabaseInstance = createClient(supabaseUrl, supabaseKey);
}

export const supabase = supabaseInstance;

/**
 * Trae TODAS las filas de una query PostgREST, paginando con .range().
 *
 * PostgREST corta toda respuesta en `max-rows` (1000 por defecto en Supabase)
 * SIN error y SIN aviso: una sola query devuelve <=1000 filas y el resto
 * desaparece en silencio. Cualquier lectura org-wide de una tabla grande
 * (transactions supera las 1000 filas) tiene que paginar o reporta de menos.
 *
 * @param makeQuery Función que CONSTRUYE una query nueva en cada llamada. Tiene
 *   que ser una factory, no un builder ya armado: el builder se consume al
 *   await-earlo y no se puede reutilizar entre páginas. Incluye aquí tu
 *   .select()/.eq()/.in()/.order() como siempre.
 * @param opts.tiebreak Columna de desempate (default 'id'). Se añade como último
 *   .order() para garantizar un orden TOTAL. Sin él, los empates del .order()
 *   principal (p.ej. created_at, que repite dentro del mismo segundo) pueden
 *   reordenarse entre páginas y duplicar o saltar filas en el borde. `id` existe
 *   en todas las tablas y sirve aunque no esté en el .select().
 *
 * Ojo: es paginación por offset sobre un snapshot; asume que los datos no cambian
 * durante el barrido (igual que cualquier carga de página). Para lecturas de
 * montaje está bien.
 */
export const FETCH_ALL_PAGE_SIZE = 1000;
const FETCH_ALL_MAX_PAGES = 50; // tope anti-bucle: 50k filas por barrido

export const fetchAllRows = async (makeQuery, { tiebreak = 'id', label = 'query' } = {}) => {
    const rows = [];
    for (let page = 0; page < FETCH_ALL_MAX_PAGES; page++) {
        let q = makeQuery();
        if (tiebreak) q = q.order(tiebreak, { ascending: true });
        const { data, error } = await q.range(rows.length, rows.length + FETCH_ALL_PAGE_SIZE - 1);
        if (error) throw error;
        if (!data || data.length === 0) return rows;
        rows.push(...data);
        if (data.length < FETCH_ALL_PAGE_SIZE) return rows;
    }
    console.warn(`[fetchAllRows] ${label}: se alcanzó el tope de ${FETCH_ALL_MAX_PAGES} páginas (${rows.length} filas); podría haber más.`);
    return rows;
};
